import React from "react";
import {Link} from "react-router-dom";
import {HashLink} from "react-router-hash-link";
import EasGlobalStyles from "../components/eas/EasGlobalStyles";
import EasReveal from "../components/eas/EasReveal";

/**
 * EAS sales page — lives at /system. Sections: nav · hero · 6-week curriculum ·
 * value stack + guarantee + enroll CTA · vs tutoring comparison · FAQ · final CTA · footer.
 *
 * The "Enroll my teenager" button is wired to a noop handler (`onEnroll` prop)
 * so you can plug in Stripe or your existing PurchaseService later.
 */
export default function EasSystem({onEnroll}: { onEnroll?: () => void } = {}): JSX.Element {
    const handleEnroll = onEnroll ?? (() => alert("Hook up Stripe / PurchaseService here."));

    return (
        <main className="eas-root">
            <EasGlobalStyles/>

            <nav style={navStyle}>
                <div className="eas-container eas-system-nav" style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 32px",
                    gap: 12
                }}>
                    <Link to="/" style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontWeight: 600,
                        fontSize: 16,
                        textDecoration: "none",
                        color: "#fff"
                    }}>
                        <img
                            src="https://produkacja.s3.eu-central-1.amazonaws.com/web/logo/logo-500-500-raw.png"
                            alt="Produkacja"
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                flexShrink: 0,
                                display: "block",
                                objectFit: "cover"
                            }}
                        />
                        <span>Effective Action System</span>
                    </Link>
                    <div style={{display: "flex", gap: 16, alignItems: "center", fontSize: 14}}>
                        <span className="eas-system-nav-price" style={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: 12,
                            color: "rgba(255,255,255,0.5)",
                            letterSpacing: "0.08em",
                            whiteSpace: "nowrap"
                        }}>$2,997 · lifetime access</span>
                        <HashLink smooth to="/system#enroll" style={{
                            background: "#2ad167",
                            color: "#0b1410",
                            padding: "9px 18px",
                            borderRadius: 999,
                            fontWeight: 600,
                            textDecoration: "none",
                            flexShrink: 0
                        }}>Enroll →</HashLink>
                    </div>
                </div>
            </nav>

            <header style={{padding: "80px 0 100px", position: "relative", overflow: "hidden", textAlign: "center"}}>
                <div style={{
                    position: "absolute",
                    top: -200,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 1200,
                    height: 800,
                    background: "radial-gradient(circle, rgba(42,209,103,0.18), transparent 60%)",
                    pointerEvents: "none",
                    filter: "blur(40px)"
                }}/>
                <div className="eas-container" style={{position: "relative"}}>
                    <span style={pillStyle}><span
                        style={{width: 6, height: 6, borderRadius: "50%", background: "#5ce08d"}}/>Now open · Worldwide</span>
                    <h1 className="eas-h1-sales" style={salesH1Style}>The full <em style={emStyle}>Effective Action
                        System</em>.</h1>
                    <p style={{
                        fontSize: 21,
                        lineHeight: 1.55,
                        maxWidth: 600,
                        margin: "0 auto 40px",
                        color: "rgba(255,255,255,0.75)"
                    }}>
                        Six weeks. Forty video lessons. The exercises, community, and weekly office hours that have
                        taken 950+ teenagers from "I'll start tomorrow" to running their own week.
                    </p>
                    <HashLink smooth to="/system#enroll" className="eas-btn eas-btn--primary eas-btn--large">Enroll for
                        $2,997 <span className="eas-arr">→</span></HashLink>
                    <div style={{
                        marginTop: 32,
                        display: "flex",
                        gap: 28,
                        justifyContent: "center",
                        flexWrap: "wrap",
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 12,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.5)"
                    }}>
                        <span>Lifetime access</span><span>·</span><span>30-day guarantee</span><span>·</span><span>1:1 onboarding call included</span>
                    </div>
                </div>
            </header>

            {/* curriculum */}
            <section className="eas-section--tight" style={{paddingTop: 0}}>
                <EasReveal>
                    <div className="eas-container">
                        <p className="eas-section-eyebrow" style={{textAlign: "center"}}>Six-week curriculum</p>
                        <h2 className="eas-h2"
                            style={{textAlign: "center", margin: "0 auto 16px", maxWidth: "24ch"}}>Every week is <em>actually
                            finishable</em>.</h2>
                        <p style={{
                            textAlign: "center",
                            fontSize: 17,
                            lineHeight: 1.6,
                            maxWidth: "60ch",
                            margin: "0 auto",
                            color: "rgba(255,255,255,0.65)"
                        }}>3–4 hours of work per week. 1.5 hours of short video, the rest is real-life exercises with
                            feedback. Designed so a 14-year-old with ADHD will actually finish.</p>
                        <div className="eas-grid-curriculum">
                            {WEEKS.map((w, i) => (
                                <div key={i} style={{background: "#131e18", padding: 28}}>
                                    <span style={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: 11,
                                        letterSpacing: "0.18em",
                                        color: "#5ce08d"
                                    }}>WEEK {String(i + 1).padStart(2, "0")}</span>
                                    <h3 style={{
                                        fontFamily: '"Fraunces", serif',
                                        fontWeight: 500,
                                        fontSize: 22,
                                        lineHeight: 1.2,
                                        margin: "14px 0 10px",
                                        color: "#fff"
                                    }}>{w.title}</h3>
                                    <p style={{
                                        fontFamily: '"Fraunces", serif',
                                        fontStyle: "italic",
                                        fontSize: 14,
                                        lineHeight: 1.5,
                                        margin: "0 0 16px",
                                        color: "rgba(255,255,255,0.7)"
                                    }}>{w.summary}</p>
                                    <ul style={{
                                        listStyle: "none",
                                        padding: 0,
                                        margin: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8
                                    }}>
                                        {w.bullets.map((b, j) => (
                                            <li key={j} style={{
                                                display: "flex",
                                                gap: 10,
                                                fontSize: 14,
                                                color: "rgba(255,255,255,0.75)"
                                            }}>
                                                <span style={{color: "#5ce08d", flexShrink: 0}}>→</span>{b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </EasReveal>
            </section>

            {/* value stack + enroll */}
            <section id="enroll" className="eas-section--tight">
                <EasReveal>
                    <div className="eas-container">
                        <div className="eas-pad-stack" style={{
                            background: "linear-gradient(180deg, #131e18, #1c2a23)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 32,
                            padding: 64
                        }}>
                            <p className="eas-section-eyebrow">What's in the box</p>
                            <h2 className="eas-h2" style={{marginBottom: 8}}>Everything they need to <em>actually do
                                this</em>.</h2>
                            <p style={{
                                fontSize: 16,
                                color: "rgba(255,255,255,0.65)",
                                maxWidth: "60ch",
                                margin: 0
                            }}>Built in Poland with 950+ teens, quietly tested abroad, and now adapted for
                                English-speaking families everywhere.</p>

                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                marginTop: 40,
                                borderTop: "1px solid rgba(255,255,255,0.08)"
                            }}>
                                {STACK.map((s, i) => (
                                    <div key={i} className="eas-grid-system-module">
                                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                                            <span style={{
                                                fontFamily: '"Fraunces", serif',
                                                fontSize: 20,
                                                fontWeight: 500,
                                                color: "#fff",
                                                letterSpacing: "-0.01em"
                                            }}>{s.title}</span>
                                            <span style={{fontSize: 14, color: "rgba(255,255,255,0.6)"}}>{s.desc}</span>
                                        </div>
                                        <span style={{
                                            fontFamily: '"JetBrains Mono", monospace',
                                            fontSize: 13,
                                            color: "rgba(255,255,255,0.4)",
                                            textDecoration: "line-through"
                                        }}>{s.price}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "baseline",
                                padding: "28px 0 4px",
                                flexWrap: "wrap",
                                gap: 16
                            }}>
                                <span style={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 12,
                                    letterSpacing: "0.18em",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.5)"
                                }}>Total stack value</span>
                                <span style={{
                                    fontFamily: '"Fraunces", serif',
                                    fontSize: 38,
                                    fontWeight: 400,
                                    color: "rgba(255,255,255,0.5)",
                                    textDecoration: "line-through",
                                    letterSpacing: "-0.02em"
                                }}>$5,385</span>
                            </div>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "baseline",
                                padding: "12px 0",
                                flexWrap: "wrap",
                                gap: 16,
                                borderTop: "2px solid #2ad167"
                            }}>
                                <span style={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 12,
                                    letterSpacing: "0.18em",
                                    textTransform: "uppercase",
                                    color: "#5ce08d"
                                }}>Your price today</span>
                                <span style={{
                                    fontFamily: '"Fraunces", serif',
                                    fontSize: 64,
                                    fontWeight: 400,
                                    color: "#5ce08d",
                                    letterSpacing: "-0.03em",
                                    lineHeight: 1
                                }}>$2,997</span>
                            </div>
                            <div style={{
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: 12,
                                color: "rgba(255,255,255,0.55)",
                                marginTop: 12
                            }}>or <strong
                                style={{color: "#5ce08d", fontFamily: '"Inter", sans-serif', fontWeight: 600}}>3 monthly
                                payments of $1,097</strong> · Flexible
                            </div>

                            <div style={{
                                marginTop: 40,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 14,
                                textAlign: "center"
                            }}>
                                <button onClick={handleEnroll}
                                        className="eas-btn eas-btn--primary eas-btn--large">Enroll my teenager <span
                                    className="eas-arr">→</span></button>
                                <p style={{
                                    fontFamily: '"Fraunces", serif',
                                    fontStyle: "italic",
                                    fontSize: 18,
                                    color: "rgba(255,255,255,0.85)",
                                    maxWidth: "50ch",
                                    lineHeight: 1.4
                                }}>Your teenager gets a system they'll use the
                                    rest of their life.</p>
                            </div>

                            <div style={{marginTop: 32}}>
                                <div className="eas-grid-guarantee">
                                    <div style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: "50%",
                                        border: "2px dashed #5ce08d",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontFamily: '"Fraunces", serif',
                                        color: "#5ce08d"
                                    }}>
                                        <span style={{
                                            fontSize: 36,
                                            fontWeight: 400,
                                            lineHeight: 1,
                                            letterSpacing: "-0.02em"
                                        }}>30</span>
                                        <span style={{
                                            fontFamily: '"JetBrains Mono", monospace',
                                            fontSize: 9,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            marginTop: 4
                                        }}>Day Guarantee</span>
                                    </div>
                                    <div>
                                        <h3 style={{
                                            fontFamily: '"Fraunces", serif',
                                            fontWeight: 500,
                                            fontSize: 22,
                                            color: "#fff",
                                            margin: "0 0 8px"
                                        }}>If it doesn't work, you don't pay.</h3>
                                        <p style={{
                                            fontSize: 15,
                                            color: "rgba(255,255,255,0.7)",
                                            margin: 0,
                                            maxWidth: "60ch"
                                        }}>Try the first two weeks. If your teen isn't more on top of their week than
                                            they were before, email us within 30 days and we refund every cent. We've
                                            never had to argue about this.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </EasReveal>
            </section>

            {/* faq */}
            <section className="eas-section--tight" style={{paddingTop: 0}}>
                <EasReveal>
                    <div className="eas-container eas-container--narrow">
                        <p className="eas-section-eyebrow">Frequently asked</p>
                        <h2 className="eas-h2">The honest <em>questions</em>.</h2>
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: 40,
                            borderTop: "1px solid rgba(255,255,255,0.08)"
                        }}>
                            {FAQ.map((f, i) => (
                                <details key={i} style={{borderBottom: "1px solid rgba(255,255,255,0.08)"}}>
                                    <summary style={{
                                        listStyle: "none",
                                        cursor: "pointer",
                                        padding: "24px 0",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 24,
                                        fontFamily: '"Fraunces", serif',
                                        fontSize: 20,
                                        fontWeight: 500,
                                        color: "#fff",
                                        letterSpacing: "-0.01em"
                                    }}>
                                        {f.q}<span style={{color: "#5ce08d", fontSize: 28, fontWeight: 300}}>+</span>
                                    </summary>
                                    <div style={{
                                        padding: "0 0 24px",
                                        fontSize: 15,
                                        lineHeight: 1.65,
                                        color: "rgba(255,255,255,0.7)",
                                        maxWidth: "70ch"
                                    }}>{f.a}</div>
                                </details>
                            ))}
                        </div>
                    </div>
                </EasReveal>
            </section>

            {/* final cta */}
            <section style={{padding: "140px 0", textAlign: "center", position: "relative", overflow: "hidden"}}>
                <div style={{
                    position: "absolute",
                    inset: 0,
                    background: "radial-gradient(ellipse at center, rgba(42,209,103,0.18), transparent 60%)",
                    pointerEvents: "none"
                }}/>
                <div className="eas-container" style={{position: "relative"}}>
                    <h2 style={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 300,
                        fontSize: "clamp(44px, 7vw, 96px)",
                        lineHeight: 0.96,
                        letterSpacing: "-0.03em",
                        margin: "0 0 24px",
                        color: "#fff"
                    }}>
                        Give them the tools.<br/><em style={emStyle}>They'll do the rest.</em>
                    </h2>
                    <p style={{
                        fontSize: 18,
                        color: "rgba(255,255,255,0.75)",
                        maxWidth: "50ch",
                        margin: "0 auto"
                    }}>Enrollment is open worldwide. Seats for office hours are limited each cohort.</p>
                    <div style={{marginTop: 36}}>
                        <button onClick={handleEnroll} className="eas-btn eas-btn--primary eas-btn--large">Enroll for
                            $2,997 <span className="eas-arr">→</span></button>
                    </div>
                    <div style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 12,
                        color: "rgba(255,255,255,0.5)",
                        marginTop: 28,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase"
                    }}>
                        <strong style={{
                            color: "#fff",
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: 16,
                            letterSpacing: 0,
                            textTransform: "none"
                        }}>$2,997</strong> · lifetime access · 30-day money-back guarantee
                    </div>
                </div>
            </section>
        </main>
    );
}

const WEEKS = [
    {
        title: "Awareness & Environment",
        summary: "The week your child gets clarity on who they are, where they're going, and why any of this matters.",
        bullets: [
            "BEST Self document — who they actually want to become",
            "The phone, decoded — they see the cost themselves",
            "Workspace designed for focus, not distraction",
            "Domination Plan — mapping their week",
            "The mindset shift that underpins everything else",
        ],
    },
    {
        title: "Foundations of Effective Action",
        summary: "Stop relying on motivation. Start running on a system that works on autopilot.",
        bullets: [
            "Beating procrastination at the root, not the symptom",
            "Chronotype — when their brain is at peak focus (and when to rest)",
            "Prioritization frameworks that match real life",
            "Focus-building techniques and the Power Map",
            "A side-effect benefit: their sleep usually fixes itself",
        ],
    },
    {
        title: "Introduction to Effective Learning",
        summary: "Studying smarter, not harder — and using AI without letting it think for them.",
        bullets: [
            "Spaced repetition, active recall, interleaving — daily, not theory",
            "AI as accelerator, not crutch — the strategic difference",
            "Scheduling review sessions that actually stick",
            "Planning effectively with AI as a tool",
            "What sleep does to freshly learned information",
        ],
    },
    {
        title: "Total Organization",
        summary: "Every tool in place — and the parts of school most students treat as something to survive.",
        bullets: [
            "Learning during class — walking out half-studied already",
            "Test & exam prep without panic",
            "Notes that are actually useful later",
            "Real breaks that prevent burnout",
            "Flashcards, pre-study routine, subject sequencing",
            "Bonus: stress management — guest lesson on teen mental health",
        ],
    },
    {
        title: "Effective Learning 2.0",
        summary: "The next level — and the subjects parents worry about most.",
        bullets: [
            "STEM thinking — math, physics, chemistry without rote memorization",
            "Flow state on demand — one focused hour beats three unfocused",
            "Inquiry-based learning and mind maps that work",
            "\"A month in a day\" — catching up on huge material in one focused session",
            "Everything they need to prepare for any exam at the highest level",
        ],
    },
    {
        title: "Next Level",
        summary: "Where everything locks in — and the skills become part of who they are.",
        bullets: [
            "Journaling and visualization that actually shifts behavior",
            "Environment, identity, and how they reinforce each other",
            "Working with limiting beliefs instead of fighting them",
            "Self-image as the foundation under every action",
            "By week six, this isn't a technique anymore. It's who they are.",
        ],
    },
];

const STACK = [
    {
        title: "Effective Action System — full six-week course",
        desc: "40+ video lessons · 6 progressive modules · lifetime access including future updates",
        price: "$1,997"
    },
    {
        title: "Full exercise library & implementation materials",
        desc: "Worksheets, cheat sheets, and the BEST Self document — active building, not passive watching",
        price: "$497"
    },
    {
        title: "Lifetime access to the Effective Action community",
        desc: "A private space of growth-minded teens worldwide — peers who take their future seriously",
        price: "$997"
    },
    {
        title: "Weekly live group coaching sessions",
        desc: "Live calls with a coach personally trained by Mik — for as long as your child is in the community",
        price: "$1,497"
    },
    {
        title: "1:1 personal onboarding call",
        desc: "Before week 1 — assess goals, motivate them, map a personalized path through the system",
        price: "$397"
    },
];

const FAQ = [
    {
        q: "What's actually in the program?",
        a: "Forty-plus video lessons across six weeks, a workbook with exercises and cheat-sheets, a private community of motivated peers, weekly group office hours with the founder, and an individual 1:1 onboarding call before week 1. All for life — including future updates."
    },
    {
        q: "How much time does it actually take?",
        a: "3–4 hours per week is ideal. About 1.5 hours of video, 1.5 hours of real-world exercise. Slower pace is fine — lifetime access means you finish on your timeline."
    },
    {q: "Can I pay in installments?", a: "Yes. Three monthly payments of $1,097."},
    {
        q: "Will this work for a teen with ADHD?",
        a: "It's one of the populations the system was specifically designed for. Lessons are short by design, the exercises are concrete, and the weekly cadence is built so a brain that struggles with executive function still gets traction."
    },
    {
        q: "What if my child doesn't want to do it?",
        a: "Your teen gets a 1:1 onboarding call with a coach who walks them through what's inside before they touch a lesson. We've never had a teen finish that call and refuse to give it a try."
    },
    {
        q: "What if it doesn't work?",
        a: "Email us within 30 days, after working through at least the first two weeks. We refund 100%, no argument, no awkwardness."
    },
    {
        q: "What ages is this for?",
        a: "12 through end of university. Younger than 12 is too young; older than 22 still benefits but is no longer our core audience."
    },
];

const navStyle: React.CSSProperties = {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "rgba(11,20,16,0.85)",
    backdropFilter: "blur(16px)",
    borderBottom: "1px solid rgba(255,255,255,0.08)"
};
const pillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 16px",
    background: "rgba(42,209,103,0.12)",
    border: "1px solid rgba(42,209,103,0.25)",
    borderRadius: 999,
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "#5ce08d",
    marginBottom: 28
};
const salesH1Style: React.CSSProperties = {
    fontFamily: '"Fraunces", serif',
    fontWeight: 300,
    fontSize: "clamp(48px, 7vw, 96px)",
    lineHeight: 0.96,
    letterSpacing: "-0.03em",
    margin: "0 auto 28px",
    color: "#fff",
    maxWidth: "16ch"
};
// className "eas-h1-sales" added on the <h1> element so we can target it from
// EasGlobalStyles for mobile-only font-size shrink without touching desktop.
const emStyle: React.CSSProperties = {fontStyle: "italic", color: "#5ce08d", fontWeight: 400};
